import React, { createContext, useContext, useState, useEffect } from "react";

const TrainingContext = createContext();

export const TrainingProvider = ({ children }) => {
  const [isTrainingEnabled, setIsTrainingEnabled] = useState(false);
  const [dismissedTips, setDismissedTips] = useState(new Set());
  const [currentTip, setCurrentTip] = useState(null);

  // Load training state from localStorage on mount
  useEffect(() => {
    const savedTrainingEnabled = localStorage.getItem(
      "socrates_training_enabled"
    );
    const savedDismissedTips = localStorage.getItem("socrates_dismissed_tips");

    if (savedTrainingEnabled !== null) {
      setIsTrainingEnabled(JSON.parse(savedTrainingEnabled));
    }

    if (savedDismissedTips) {
      setDismissedTips(new Set(JSON.parse(savedDismissedTips)));
    }
  }, []);

  // Save training state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "socrates_training_enabled",
      JSON.stringify(isTrainingEnabled)
    );
  }, [isTrainingEnabled]);

  useEffect(() => {
    localStorage.setItem(
      "socrates_dismissed_tips",
      JSON.stringify([...dismissedTips])
    );
  }, [dismissedTips]);

  const toggleTraining = () => {
    setIsTrainingEnabled(!isTrainingEnabled);
    // Reset dismissed tips when enabling training
    if (!isTrainingEnabled) {
      setDismissedTips(new Set());
    }
  };

  const dismissTip = (tipId, onDismissCallback = null) => {
    setDismissedTips((prev) => new Set([...prev, tipId]));
    if (currentTip?.id === tipId) {
      setCurrentTip(null);
    }
    // Call the callback if provided (for chaining tips)
    if (onDismissCallback && typeof onDismissCallback === "function") {
      setTimeout(onDismissCallback, 300); // Small delay for smooth UX
    }
  };

  const showTip = (tip) => {
    if (isTrainingEnabled && !dismissedTips.has(tip.id)) {
      setCurrentTip(tip);
    }
  };

  const closeTip = (onCloseCallback = null) => {
    setCurrentTip(null);
    // Call the callback if provided (for chaining tips)
    if (onCloseCallback && typeof onCloseCallback === "function") {
      setTimeout(onCloseCallback, 300); // Small delay for smooth UX
    }
  };

  const resetAllTips = () => {
    setDismissedTips(new Set());
    localStorage.removeItem("socrates_dismissed_tips");
  };

  const value = {
    isTrainingEnabled,
    toggleTraining,
    dismissedTips,
    dismissTip,
    currentTip,
    showTip,
    closeTip,
    resetAllTips,
  };

  return (
    <TrainingContext.Provider value={value}>
      {children}
    </TrainingContext.Provider>
  );
};

export const useTraining = () => {
  const context = useContext(TrainingContext);
  if (!context) {
    throw new Error("useTraining must be used within a TrainingProvider");
  }
  return context;
};
